import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  Info,
  ArrowDownToLine,
  ArrowUpFromLine,
  X,
  Loader2,
} from "lucide-react";
import { useLanguage } from "./LanguageContext";
import { getMyNotifications, markNotificationRead, removeNotification as apiRemoveNotification } from "@/shared/api/notifications";

const typeConfig = {
  success: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-50" },
  warning: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-50" },
  info: { icon: Info, color: "text-blue-500", bg: "bg-blue-50" },
  deposit: { icon: ArrowDownToLine, color: "text-blue-500", bg: "bg-blue-50" },
  withdrawal: { icon: ArrowUpFromLine, color: "text-emerald-500", bg: "bg-emerald-50" },
};

function mapFromApi(n) {
  return {
    id: n.id,
    title: n.title,
    message: n.message,
    type: n.type,
    read: n.isRead,
    time: new Date(n.createdAt),
  };
}

export default function NotificationDropdown({ accentColor = "blue" }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getMyNotifications();
      setNotifications(list.map(mapFromApi));
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (_) {}
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    try {
      await Promise.all(unreadIds.map((id) => markNotificationRead(id)));
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (_) {}
  };

  const removeNotification = async (id) => {
    try {
      const ok = await apiRemoveNotification(id);
      if (ok) setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (_) {}
  };

  const accentColors = {
    blue: "bg-blue-500",
    violet: "bg-violet-500",
    sky: "bg-sky-500",
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className={`absolute top-1 right-1 w-2 h-2 ${accentColors[accentColor] || accentColors.blue} rounded-full`} />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm">{t("notifications")}</h3>
            {unreadCount > 0 && (
              <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>
          {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                {t("markAllRead")}
              </button>
            )}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-6 flex justify-center items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">
              No notifications
            </div>
          ) : (
            notifications.map((notification) => {
              const config = typeConfig[notification.type?.toLowerCase()] || typeConfig.info;
              const Icon = config.icon;

              return (
                <div
                  key={notification.id}
                  onClick={() => markAsRead(notification.id)}
                  className={`flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 transition-colors ${
                    !notification.read ? "bg-blue-50/50" : ""
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium ${!notification.read ? "text-slate-900" : "text-slate-600"}`}>
                        {notification.title}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        className="text-slate-400 hover:text-slate-600 p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {format(notification.time, "h:mm a")}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="p-2 border-t bg-slate-50">
          <button className="w-full text-center text-xs text-slate-600 hover:text-slate-900 py-1.5 font-medium">
            {t("viewAllNotifications")}
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}